import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DecimalField from '../shared/DecimalField';

// Label roll sizes the backend renders (maintenances/services/part_labels.py).
const LABEL_SIZES = ['57x32', '50x25', '102x74'];
const MAX_COPIES = 50;
// Remembered per browser: a workshop prints on the same roll every time.
const SIZE_STORAGE_KEY = 'part_label_size';

const readStoredSize = () => {
  try {
    const stored = localStorage.getItem(SIZE_STORAGE_KEY);
    return LABEL_SIZES.includes(stored) ? stored : LABEL_SIZES[0];
  } catch {
    return LABEL_SIZES[0];
  }
};

/**
 * Print barcode / price labels (hintalaput) for one or more parts.
 *
 * The backend returns a page with one printed page per label at the label's size.
 * Like the tire hotel labels (tire_hotel/tireLabel.js), it is written into a new
 * window and the browser's print dialog opens on it straight away.
 */
function PrintPartLabelsDialog({ open, onClose, partIds }) {
  const { t } = useTranslation();
  const [size, setSize] = useState(readStoredSize);
  const [showPrice, setShowPrice] = useState(true);
  const [copies, setCopies] = useState('1');
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState(null);
  // The label window waits here until this dialog has fully closed, then prints.
  // In Firefox print() does not return while the print dialog is open, and this
  // page pauses its timers meanwhile; Verstaani must stay usable all that time:
  // - printing while the dialog was still closing left its invisible backdrop
  //   over the page, so wait until React has removed it (the effect below);
  // - printing from inside React (the effect itself) stalled React's update loop,
  //   so clicks did nothing, so the call goes to a plain timer, outside React,
  //   which is where the tire hotel labels call it from too.
  const pendingPrintWindow = useRef(null);
  const [windowToPrint, setWindowToPrint] = useState(null);

  useEffect(() => {
    if (!windowToPrint) return;
    setWindowToPrint(null);
    setTimeout(() => {
      if (!windowToPrint.closed) windowToPrint.print();
    }, 0);
  }, [windowToPrint]);

  const handleExited = () => {
    if (pendingPrintWindow.current) {
      setWindowToPrint(pendingPrintWindow.current);
      pendingPrintWindow.current = null;
    }
  };

  useEffect(() => {
    if (open) {
      setError(null);
      setCopies('1');
    }
  }, [open]);

  const count = partIds?.length || 0;

  const handlePrint = async () => {
    // Open the window inside the click: opened only after the request returns, a
    // browser may treat it as an unrequested pop-up and block it.
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError(t('partLabels.popupBlocked'));
      return;
    }
    setPrinting(true);
    setError(null);
    try {
      const response = await axios.post(
        '/api/parts/labels/',
        { part_ids: partIds, size, show_price: showPrice, copies: parseInt(copies, 10) || 1 },
        { responseType: 'text' }
      );
      try {
        localStorage.setItem(SIZE_STORAGE_KEY, size);
      } catch {
        // Storage unavailable: the size just is not remembered.
      }
      printWindow.document.open();
      printWindow.document.write(response.data);
      printWindow.document.close();
      printWindow.document.title = t('partLabels.windowTitle');
      // Print once the dialog is gone (handleExited), not now: see pendingPrintWindow.
      pendingPrintWindow.current = printWindow;
      onClose();
    } catch (err) {
      printWindow.close();
      // Only the error code matters; it maps to our own translated text.
      let errorCode = null;
      try {
        const data = err.response?.data;
        errorCode = (typeof data === 'string' ? JSON.parse(data) : data)?.error_code;
      } catch {
        // Not JSON: fall through to the generic message.
      }
      setError(errorCode === 'too_many_labels' ? t('partLabels.tooMany') : t('partLabels.failed'));
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={printing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: '12px', sm: '16px' }, m: { xs: 2, sm: 4 } } }}
      TransitionProps={{ onExited: handleExited }}
      data-testid="print-part-labels-dialog"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {count === 1 ? t('partLabels.titleOne') : t('partLabels.title', { count })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{t('partLabels.size')}</InputLabel>
            <Select
              value={size}
              label={t('partLabels.size')}
              onChange={(e) => setSize(e.target.value)}
              data-testid="part-label-size"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="57x32">57 × 32 mm</MenuItem>
              <MenuItem value="50x25">50 × 25 mm</MenuItem>
              <MenuItem value="102x74">102 × 74 mm</MenuItem>
            </Select>
          </FormControl>
          <DecimalField
            label={t('partLabels.copies')}
            value={copies}
            onChange={setCopies}
            maxDecimals={0}
            max={MAX_COPIES}
            fullWidth
            inputProps={{ 'data-testid': 'part-label-copies' }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
          <FormControlLabel
            control={<Checkbox checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} />}
            label={t('partLabels.showPrice')}
          />
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{t('partLabels.help')}</Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={printing} sx={{ borderRadius: '10px', px: 3 }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          disabled={printing || count === 0 || !(parseInt(copies, 10) >= 1)}
          startIcon={printing ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
          data-testid="print-part-labels-submit"
          sx={{ borderRadius: '10px', px: 3 }}
        >
          {t('Print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PrintPartLabelsDialog;
